import {
  createMarkdownCard,
  createSectionedHorizontalCard,
} from '@/lib/idea-card-factory';
import type {
  IdeaCardBodyMode,
  IdeaCardBodySection,
  IdeaCardInputData,
  IdeaCardLayout,
  IdeaCardPreviewData,
  IdeaCardPreviewType,
} from '@/lib/idea-card-types';

const FORMAT_BLOCK_METADATA_EXCLUDE_KEYS = ['layout', 'type', 'title', 'body_mode', 'preview_label', 'groupLabel'];

export interface FormatBlockMetadata {
  layout?: IdeaCardLayout;
  preview?: IdeaCardPreviewData;
  bodyMode: IdeaCardBodyMode;
  groupLabel?: string;
}

export const resolveFormatBlockMetadata = (metadata: Record<string, string>): FormatBlockMetadata => {
  const previewType = metadata.type?.trim() as IdeaCardPreviewType | undefined;
  const preview = previewType
    ? {
        type: previewType,
        label: metadata.preview_label?.trim() || undefined,
        props: Object.fromEntries(
          Object.entries(metadata).filter(([key]) => !FORMAT_BLOCK_METADATA_EXCLUDE_KEYS.includes(key))
        ),
      }
    : undefined;

  const rawBodyMode = metadata.body_mode?.trim().toLowerCase();
  const bodyMode: IdeaCardBodyMode =
    rawBodyMode === 'below' || rawBodyMode === 'hidden'
      ? rawBodyMode
      : preview
        ? 'hidden'
        : 'below';

  return {
    layout: metadata.layout as IdeaCardLayout | undefined,
    preview,
    bodyMode,
    groupLabel: metadata.groupLabel?.trim() || undefined,
  };
};

export const createFormatBlockCard = (
  title: string,
  metadata: FormatBlockMetadata,
  content: { body?: string; bodySections?: IdeaCardBodySection[] }
): IdeaCardInputData => {
  if (content.bodySections && content.bodySections.length > 0) {
    return createSectionedHorizontalCard({
      title,
      bodySections: content.bodySections,
      bodyMode: metadata.bodyMode,
      preview: metadata.preview,
      groupLabel: metadata.groupLabel,
    });
  }

  return createMarkdownCard({
    title,
    body: content.body,
    layout: metadata.layout,
    bodyMode: metadata.bodyMode,
    preview: metadata.preview,
    groupLabel: metadata.groupLabel,
  });
};
